<template>
  <v-row class="px-5 pt-16 status-page" justify="center">
    <v-col xl="8" lg="8" md="10" sm="12" cols="12">
      <a href="/" class="no-decor">Back to SmartTMS</a>

      <v-card class="pa-6 mt-4 elevation-2">
        <label class="api-name">Suggest Freight Class</label>
        <v-alert type="warning" prominent class="mt-4 custom-alert">
          <strong>Important:</strong> The NMFC calculation logic for determining
          freight classes will be updated on July 19th 2025. This update will
          affect how freight classes are determined based on new rules. This API
          will automatically apply new rules starting July 19th 2025, so if you
          call it before rating, booking, or add pending order APIs you don't
          have to worry about manual calculation of freight class.
        </v-alert>

        <p class="mt-4">
          This API endpoint allows you to get a suggested freight class based on
          the dimensions and weight of your shipment. The freight class is
          determined using NMFC calculation logic, which considers the density
          of the shipment.
          <br />
          <br />
          This endpoint is particularly useful when you need to determine the
          appropriate freight class for your shipment before booking it with a
          carrier.
        </p>

        <!-- API Endpoint and HTTP Verb -->
        <v-row class="endpoint-row">
          <v-col cols="12">
            <span class="label"
              >API endpoint:
              <code>[POST] {{ baseUrl }}/freight-classes/suggest</code></span
            >
            <span class="value"></span>
          </v-col>
        </v-row>

        <label class="mt-8 sub-section">HTTP Headers</label>
        <v-divider class="mt-2 mb-6"></v-divider>

        <v-row>
          <v-col cols="12">
            <span class="label"
              ><code>Authorization: Bearer &lt;access token&gt;</code></span
            >
          </v-col>
          <v-col cols="12" class="mt-n2">
            <span class="label"
              ><code>Content-Type: application/json</code></span
            >
          </v-col>
        </v-row>

        <br />
        <label class="mt-8 sub-section">Request Fields</label>

        <v-divider class="mt-2 mb-6"></v-divider>

        <v-row class="table-header mx-1">
          <v-col class="field-col" cols="2">Field</v-col>
          <v-col class="type-col" cols="2">Type</v-col>
          <v-col class="require-col" cols="2">Required</v-col>
          <v-col class="description-col" cols="6">Description</v-col>
        </v-row>

        <!-- height -->
        <v-row class="table-row mx-1">
          <v-col class="field-col" cols="2">height</v-col>
          <v-col class="type-col" cols="2">integer</v-col>
          <v-col class="require-col" cols="2">yes</v-col>
          <v-col class="description-col" cols="6">
            The height of the shipment item in the specified units.
            <br />
            Example:
            <pre><code>11</code></pre>
          </v-col>
        </v-row>

        <!-- width -->
        <v-row class="table-row mx-1">
          <v-col class="field-col" cols="2">width</v-col>
          <v-col class="type-col" cols="2">integer</v-col>
          <v-col class="require-col" cols="2">yes</v-col>
          <v-col class="description-col" cols="6">
            The width of the shipment item in the specified units.
            <br />
            Example:
            <pre><code>14</code></pre>
          </v-col>
        </v-row>

        <!-- length -->
        <v-row class="table-row mx-1">
          <v-col class="field-col" cols="2">length</v-col>
          <v-col class="type-col" cols="2">integer</v-col>
          <v-col class="require-col" cols="2">yes</v-col>
          <v-col class="description-col" cols="6">
            The length of the shipment item in the specified units.
            <br />
            Example:
            <pre><code>13</code></pre>
          </v-col>
        </v-row>

        <!-- units -->
        <v-row class="table-row mx-1">
          <v-col class="field-col" cols="2">units</v-col>
          <v-col class="type-col" cols="2">integer</v-col>
          <v-col class="require-col" cols="2">yes</v-col>
          <v-col class="description-col" cols="6">
            The unit of measurement for the dimensions.
            <br />
            Example:
            <pre><code>1</code></pre>
          </v-col>
        </v-row>

        <!-- weight -->
        <v-row class="table-row mx-1">
          <v-col class="field-col" cols="2">weight</v-col>
          <v-col class="type-col" cols="2">integer</v-col>
          <v-col class="require-col" cols="2">yes</v-col>
          <v-col class="description-col" cols="6">
            The total weight of the shipment item.
            <br />
            Example:
            <pre><code>200</code></pre>
          </v-col>
        </v-row>

        <br />
        <label class="mt-8 sub-section">Example Request</label>
        <v-divider class="mt-2 mb-6"></v-divider>

        <v-row>
          <v-col cols="12">
            <pre><code>{
  "height": 11,
  "width": 14,
  "length": 13,
  "units": 1,
  "weight": 200
}</code></pre>
          </v-col>
        </v-row>

        <br />
        <label class="mt-8 sub-section">Response Fields</label>
        <v-divider class="mt-2 mb-6"></v-divider>

        <v-row class="table-header mx-1">
          <v-col class="field-col" cols="2">Field</v-col>
          <v-col class="type-col" cols="2">Type</v-col>
          <v-col class="description-col" cols="8">Description</v-col>
        </v-row>

        <!-- data.suggestedClass -->
        <v-row class="table-row mx-1">
          <v-col class="field-col" cols="2">data.suggestedClass</v-col>
          <v-col class="type-col" cols="2">decimal</v-col>
          <v-col class="description-col" cols="8">
            The suggested freight class for the shipment based on the provided
            dimensions and weight.
            <br />
            Example:
            <pre><code>77.5</code></pre>
          </v-col>
        </v-row>

        <br />
        <label class="mt-8 sub-section">Example Response</label>
        <v-divider class="mt-2 mb-6"></v-divider>

        <v-row>
          <v-col cols="12">
            <pre><code>{
  "data": {
    "suggestedClass": 77.5
  }
}</code></pre>
          </v-col>
        </v-row>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
export default {
  name: 'SuggestFreightClass',
  computed: {
    baseUrl() {
      let baseUrl = '/api';

      const href = window.location.href;
      if (href.includes('.uat.')) {
        baseUrl = 'https://api.uat.afs.net/smart-tms';
      } else if (href.includes('smarttms.afs.net')) {
        baseUrl = 'https://api.afs.net/smart-tms';
      }

      return baseUrl;
    },
  },
};
</script>

<style lang="scss" scoped>
.api-name {
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  font-size: 32px !important;
  line-height: 38px;
  letter-spacing: -0.01em !important;
  color: #0b436a;
}

.sub-section {
  font-family: 'Poppins';
  font-style: normal;
  font-weight: 600;
  font-size: 26px !important;
  line-height: 38px;
  letter-spacing: -0.01em !important;
  color: #0b436a;
}

.table-header th {
  text-align: left;
  font-weight: 600;
  background-color: #e3f2fd;
  color: #0b436a;
  padding: 8px;
}

.table-header {
  background-color: #e3f2fd;
  font-weight: 600;
  color: #0b436a;
  border-bottom: 2px solid #0b436a;
  padding: 8px 0;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
}

.table-row {
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 0;
}

.field-col {
  width: auto;
  word-wrap: break-word;
}

.type-col {
  width: auto;
  word-wrap: break-word;
}

.require-col {
  width: auto;
  word-wrap: break-word;
}

.length-col {
  width: auto;
  word-wrap: break-word;
}

.description-col {
  width: auto;
  word-wrap: break-word;
}

.v-card {
  border-radius: 12px;
  background: #ffffff;
}

.v-divider {
  background-color: #0b436a;
  height: 2px;
}

.no-decor {
  text-decoration: none;
}

.headers-row {
  margin-bottom: 16px;
}

.headers-title {
  font-weight: 600;
  color: #0b436a;
  font-size: 18px;
  margin-bottom: 8px;
}

.headers-row .label {
  font-size: 14px;
}

.headers-row .value {
  font-size: 14px;
  margin-left: 8px;
}

.endpoint-row {
  margin-bottom: 16px;
}

.endpoint-row .label {
  font-size: 16px;
}

.endpoint-row .value {
  font-size: 16px;
}

pre {
  background-color: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  overflow-x: auto;
}

code {
  font-family: 'Courier New', Courier, monospace;
}

.custom-alert {
  border-radius: 16px !important;
}
</style>
